import { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, Printer, ShieldAlert, Activity, Stethoscope, HeartPulse, Users, Accessibility, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { openPrintWindow } from '@/lib/letterhead';
import JsBarcode from 'jsbarcode';

const PARAM_LABELS = {
  PA_sistolica: 'PA Sist.', PA_diastolica: 'PA Diast.', FC: 'FC', FR: 'FR',
  SpO2: 'SpO2', temperatura: 'Temp.', glicemia: 'Glicemia', peso: 'Peso'
};
const PARAM_UNITS = {
  PA_sistolica: 'mmHg', PA_diastolica: 'mmHg', FC: 'bpm', FR: 'atti/min',
  SpO2: '%', temperatura: 'C', glicemia: 'mg/dL', peso: 'kg'
};
const NUMERICI = Object.keys(PARAM_LABELS);

const dataIt = (v) => v ? new Date(v).toLocaleDateString('it-IT') : '-';

function buildVitalsSection(parametri, giorni) {
  const numerici = (parametri || []).filter(p => NUMERICI.includes(p.tipo));
  if (numerici.length === 0) return '';
  let html = `<p class="section-title">Parametri Vitali (ultimi ${giorni} giorni)</p>`;
  const byDate = {};
  numerici.forEach(p => {
    const d = p.data_rilevazione?.slice(0, 10) || '';
    if (!byDate[d]) byDate[d] = {};
    byDate[d][p.tipo] = p.valore;
    if (p.fuori_range) byDate[d][`${p.tipo}_alert`] = true;
  });
  const dates = Object.keys(byDate).sort();
  const typesPresent = [...new Set(numerici.map(p => p.tipo))].filter(t => t !== 'PA_diastolica');

  html += '<table><thead><tr><th>Data</th>';
  typesPresent.forEach(t => {
    const label = t === 'PA_sistolica' ? 'PA (mmHg)' : `${PARAM_LABELS[t] || t} (${PARAM_UNITS[t] || ''})`;
    html += `<th>${label}</th>`;
  });
  html += '</tr></thead><tbody>';
  dates.forEach(d => {
    const vals = byDate[d];
    const dateLabel = new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
    html += `<tr><td style="font-weight:bold">${dateLabel}</td>`;
    typesPresent.forEach(t => {
      if (t === 'PA_sistolica') {
        const alert = vals.PA_sistolica_alert || vals.PA_diastolica_alert;
        html += `<td style="${alert ? 'color:red;font-weight:bold' : ''}">${vals.PA_sistolica || '-'}/${vals.PA_diastolica || '-'}</td>`;
      } else {
        const v = vals[t];
        html += `<td style="${vals[`${t}_alert`] ? 'color:red;font-weight:bold' : ''}">${v != null ? v : '-'}</td>`;
      }
    });
    html += '</tr>';
  });
  html += '</tbody></table>';
  return html;
}

export default function EmergencyPrintButton({ ospiteId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const [res, atcRes] = await Promise.all([
        api.get(`/ospiti/${ospiteId}/scheda-emergenza`),
        api.get('/utility/atc-categories').catch(() => ({ data: {} }))
      ]);
      const atcMap = atcRes.data || {};
      const d = res.data;
      const arricchisci = (lista) => (lista || []).map(a => typeof a === 'string' ? a : ({
        ...a, categoria_atc_nome: a.categoria_atc ? atcMap[a.categoria_atc] || a.categoria_atc : ''
      }));
      d.allergie = arricchisci(d.allergie);
      d.allergie_non_farmacologiche = arricchisci(d.allergie_non_farmacologiche);
      setData(d);
    } catch { toast.error('Errore caricamento dati emergenza'); }
    setLoading(false);
  };

  const o = data?.ospite;
  const terapie = data?.terapie_attive || [];
  const critici = data?.farmaci_critici || [];
  const allergie = data?.allergie || [];
  const allergieNonFarma = data?.allergie_non_farmacologiche || [];
  const documenti = data?.documenti_identita || [];
  const parametri = data?.parametri_vitali || [];
  const giorni = data?.giorni_parametri || 10;
  const diagnosi = data?.diagnosi || {};
  const presidi = data?.presidi || [];
  const decisori = data?.decisori || [];
  const autonomia = data?.autonomia || {};
  const contenzioni = data?.contenzioni || [];
  const dimissioni = data?.dimissioni || [];
  const allerta = data?.allerta;
  const medico = data?.medico || {};
  const numeriUtili = data?.numeri_utili || [];
  const areaLabel = o?.area === 'verde' ? 'AREA VERDE' : o?.area === 'gialla' ? 'AREA GIALLA' : '';

  const lastVitals = {};
  parametri.filter(p => NUMERICI.includes(p.tipo)).forEach(p => { if (!lastVitals[p.tipo]) lastVitals[p.tipo] = p; });

  const handlePrint = () => {
    if (!o) return;
    const stampa = new Date();

    let body = `<div style="display:flex;align-items:center;gap:10px;padding:8px 12px;border:2px solid #dc2626;margin-bottom:10px;border-radius:6px">
      <div style="flex:1">
        <p style="margin:0;font-weight:bold;color:#dc2626;font-size:13px">SCHEDA EMERGENZA - ${o.cognome?.toUpperCase()} ${o.nome?.toUpperCase()}</p>
        <p style="margin:2px 0 0;font-size:10px;color:#475569">Nata/o il ${dataIt(o.data_nascita)}${o.luogo_nascita ? ' a ' + o.luogo_nascita : ''} · Camera ${o.stanza} letto ${o.letto}${areaLabel ? ' · ' + areaLabel : ''}</p>
      </div>
      ${o.foto_base64 ? `<img src="${o.foto_base64}" style="width:70px;height:88px;object-fit:cover;border:1px solid #cbd5e1;border-radius:4px" />` : ''}
    </div>`;

    // ALLERGIE - sempre presenti
    body += `<div style="padding:8px;background:#fef2f2;border:3px solid #dc2626;border-radius:8px;margin-bottom:10px">`;
    body += `<p style="font-weight:bold;color:#dc2626;font-size:12px;margin:0 0 4px;text-transform:uppercase">Allergie / Intolleranze</p>`;
    if (allergie.length > 0 || allergieNonFarma.length > 0) {
      body += `<table style="width:100%"><thead><tr><th style="text-align:left;color:#dc2626">Sostanza</th><th style="text-align:left;color:#dc2626">Tipo / Categoria</th><th style="text-align:left;color:#dc2626">Reazione</th><th style="text-align:left;color:#dc2626">Gravita</th></tr></thead><tbody>`;
      [...allergie, ...allergieNonFarma].forEach(a => {
        if (typeof a === 'string') {
          body += `<tr><td colspan="4" style="font-weight:bold;color:#dc2626">${a}</td></tr>`;
        } else {
          const cat = a.categoria_atc_nome || a.tipo || '-';
          body += `<tr><td style="font-weight:bold">${a.sostanza || '-'}</td><td>${cat}</td><td>${a.reazione || '-'}</td><td style="font-weight:bold;color:${a.gravita === 'grave' ? '#dc2626' : '#ea580c'}">${a.gravita || '-'}</td></tr>`;
        }
      });
      body += `</tbody></table>`;
    } else {
      body += `<p style="margin:0;font-weight:bold;color:#dc2626;font-size:11px">NESSUNA ALLERGIA DICHIARATA — l'assenza di allergie non e accertata: verificare prima di somministrare</p>`;
    }
    body += `</div>`;

    // Farmaci critici
    if (critici.length > 0) {
      body += `<div style="padding:8px;background:#fff7ed;border:2px solid #ea580c;border-radius:8px;margin-bottom:10px">`;
      body += `<p style="font-weight:bold;color:#c2410c;font-size:12px;margin:0 0 4px;text-transform:uppercase">Anticoagulanti / Antiaggreganti / Insulina in corso</p>`;
      body += `<table style="width:100%"><tbody>`;
      critici.forEach(f => {
        body += `<tr><td style="font-weight:bold;width:110px">${f.classe}</td><td style="font-weight:bold">${f.farmaco}</td><td>${f.dosaggio || '-'}</td><td>${f.al_bisogno ? 'al bisogno' : (f.orari || []).join(', ') || '-'}</td></tr>`;
      });
      body += `</tbody></table></div>`;
    }

    // Allerta clinica
    if (allerta?.news2?.calcolabile || (allerta?.alert || []).length > 0) {
      body += `<p class="section-title">Allerta Clinica</p><ul style="margin:0 0 6px 14px">`;
      if (allerta.news2?.calcolabile) {
        body += `<li style="font-size:10px"><strong>NEWS2 = ${allerta.news2.punteggio}</strong> (${allerta.news2.livello}) — ${allerta.news2.indicazione || ''}</li>`;
      }
      (allerta.alert || []).forEach(a => {
        body += `<li style="font-size:10px"><strong>${a.titolo}</strong>${a.dettaglio ? ' — ' + a.dettaglio : ''}</li>`;
      });
      body += `</ul>`;
    }

    // Diagnosi e anamnesi
    const vociDiagnosi = [
      ...(diagnosi.principali || []).map(d => ['Diagnosi principale', d]),
      ...(diagnosi.patologie_pai || []).map(d => ['Patologia (PAI)', d]),
      ...(diagnosi.secondarie || []).map(d => ['Diagnosi secondaria', d]),
      ...(diagnosi.anamnesi || []).map(d => ['Anamnesi', d]),
    ];
    if (vociDiagnosi.length > 0) {
      body += `<p class="section-title">Diagnosi e Anamnesi</p><table><tbody>`;
      vociDiagnosi.forEach(([etichetta, testo]) => {
        body += `<tr><td style="width:130px;font-size:9px;color:#64748b">${etichetta}</td><td style="font-size:10px">${testo}</td></tr>`;
      });
      body += `</tbody></table>`;
    }

    // Presidi e dispositivi
    if (presidi.length > 0 || data.presidi_note) {
      body += `<p class="section-title">Presidi e Dispositivi in Atto</p>`;
      body += `<p style="font-size:10px;margin:0">${presidi.join(' · ')}${data.presidi_note ? `<br/><span style="color:#64748b">${data.presidi_note}</span>` : ''}</p>`;
    }

    // Contenzioni
    if (contenzioni.length > 0) {
      body += `<p class="section-title">Contenzioni in Atto</p><table><tbody>`;
      contenzioni.forEach(c => {
        body += `<tr><td style="font-weight:bold">${c.tipo}</td><td>${c.fascia || '-'}</td><td style="font-size:9px">${c.motivazione || ''}</td></tr>`;
      });
      body += `</tbody></table>`;
    }

    // Autonomia e comunicazione
    const vociAutonomia = [];
    if (autonomia.barthel) vociAutonomia.push(`Barthel ${autonomia.barthel.punteggio}/100 — ${autonomia.barthel.classe} (${dataIt(autonomia.barthel.data)})`);
    if (autonomia.coscienza) vociAutonomia.push(`Stato di coscienza (AVPU): ${autonomia.coscienza}`);
    if (autonomia.peso != null) vociAutonomia.push(`Peso ${autonomia.peso} kg (${dataIt(autonomia.peso_data)})`);
    if (vociAutonomia.length > 0) {
      body += `<p class="section-title">Autonomia e Comunicazione</p>`;
      body += `<p style="font-size:10px;margin:0">${vociAutonomia.join(' · ')}</p>`;
    }

    // Medico di base e numeri utili
    body += `<p class="section-title">Medico di Base e Numeri Utili</p><table><tbody>`;
    body += `<tr><td style="width:150px;font-weight:bold">Medico di base</td><td>${medico.nome || o.medico_base || 'non indicato'}${medico.telefono ? ` — <strong>${medico.telefono}</strong>` : ''}${medico.ambulatorio ? ` — ${medico.ambulatorio}` : ''}</td></tr>`;
    numeriUtili.forEach(n => {
      body += `<tr><td style="width:150px;font-weight:bold">${n.nome}</td><td>${n.telefono ? `<strong>${n.telefono}</strong>` : ''}${n.note ? ` ${n.note}` : ''}</td></tr>`;
    });
    body += `</tbody></table>`;

    // Chi decide
    body += `<p class="section-title">Contatti di Emergenza e Rappresentanza Legale</p>`;
    if (decisori.length > 0) {
      body += `<table><thead><tr><th>Ruolo</th><th>Nome</th><th>Telefono</th></tr></thead><tbody>`;
      decisori.forEach(r => {
        body += `<tr><td style="font-weight:bold;text-transform:uppercase">${r.parentela}</td><td>${r.nome}</td><td style="font-weight:bold">${r.telefono || '-'}</td></tr>`;
      });
      body += `</tbody></table>`;
    }
    if (o.referenti?.length > 0) {
      body += `<table><thead><tr><th>Nome</th><th>Parentela</th><th>Telefono</th><th>Email</th></tr></thead><tbody>`;
      o.referenti.forEach(r => { body += `<tr><td><strong>${r.nome}</strong></td><td>${r.parentela}</td><td style="font-weight:bold">${r.telefono || '-'}</td><td>${r.email || '-'}</td></tr>`; });
      body += `</tbody></table>`;
    } else {
      body += `<div style="padding:8px;border:2px solid #dc2626;color:#dc2626;font-weight:bold;text-align:center;font-size:11px">NESSUN CONTATTO DI EMERGENZA REGISTRATO</div>`;
    }
    const c = o.consensi || {};
    body += `<p style="font-size:9px;color:#64748b;margin:4px 0 0">Consensi: privacy ${c.privacy ? 'si' : 'no'} · trattamenti ${c.trattamenti ? 'si' : 'no'} · dati sanitari ${c.trattamento_dati_sanitari ? 'si' : 'no'} · comunicazione familiari ${c.comunicazione_familiari ? 'si' : 'no'}${c.data_firma ? ` (firmati il ${dataIt(c.data_firma)})` : ''}</p>`;

    // Ultimo ricovero / dimissione
    if (dimissioni.length > 0) {
      body += `<p class="section-title">Ultimi Ricoveri e Lettere di Dimissione</p><table><tbody>`;
      dimissioni.forEach(d => {
        body += `<tr><td style="width:150px;font-weight:bold">${d.tipo_label}</td><td>${dataIt(d.data_documento)}${d.struttura ? ' — ' + d.struttura : ''}${d.titolo ? ` (${d.titolo})` : ''}</td></tr>`;
      });
      body += `</tbody></table>`;
    }

    // Terapie
    body += `<p class="section-title">Terapie in Corso (${terapie.length})</p>`;
    if (terapie.length > 0) {
      body += `<table><thead><tr><th>Farmaco</th><th>Dosaggio</th><th>Via</th><th>Orari</th><th>Giorni</th><th>Inizio</th><th>Note</th></tr></thead><tbody>`;
      terapie.forEach(t => {
        const orariLabel = t.al_bisogno ? '<span class="badge badge-amber">AL BISOGNO</span>' : (t.orario || (t.orari || []).join(', ') || '-');
        const allDays = ['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'];
        const g = t.giorni_settimana || allDays;
        const isEveryDay = !t.giorni_settimana || g.length === 7 || t.schema_giorni === 'tutti';
        const giorniLabel = isEveryDay ? 'Tutti i giorni' : g.map(x => x.charAt(0).toUpperCase() + x.slice(1)).join(', ');
        let dosLabel = t.dosaggio;
        if (t.dosaggi_per_orario && t.dosaggi_per_orario.length > 1) {
          const uniq = [...new Set(t.dosaggi_per_orario.map(d => d.quantita))];
          if (uniq.length > 1) {
            dosLabel = t.dosaggi_per_orario.map(d => `<span style="font-size:9px;background:#eff6ff;padding:1px 3px;border-radius:2px;font-family:monospace">${d.orario}</span> ${d.quantita}`).join('<br/>');
          }
        }
        body += `<tr><td><strong>${t.farmaco}</strong></td><td>${dosLabel}</td><td>${t.via_somministrazione || t.via || '-'}</td><td>${orariLabel}</td><td style="font-size:9px">${giorniLabel}</td><td style="font-size:9px">${t.durata_inizio ? dataIt(t.durata_inizio) : '-'}</td><td style="font-size:9px">${t.note || '-'}</td></tr>`;
      });
      body += `</tbody></table>`;
    } else {
      body += `<p style="font-size:10px;color:#64748b">Nessuna terapia attiva</p>`;
    }

    body += buildVitalsSection(parametri, giorni);

    // Codice fiscale e barcode
    if (o.codice_fiscale) {
      body += `<p class="section-title">Codice Fiscale</p>`;
      body += `<div style="margin:6px 0;padding:8px;border:1px solid #cbd5e1;border-radius:6px;display:inline-block">`;
      body += `<p style="font-family:'Courier New',monospace;font-size:16px;font-weight:bold;letter-spacing:2px;color:#1e293b;margin:0 0 4px">${o.codice_fiscale}</p>`;
      try {
        const canvas = document.createElement('canvas');
        JsBarcode(canvas, o.codice_fiscale, {
          format: 'CODE128', width: 1.4, height: 38, displayValue: true,
          fontSize: 9, textMargin: 1, margin: 10, background: '#ffffff'
        });
        body += `<img src="${canvas.toDataURL('image/png')}" style="display:block;width:5.5cm;height:auto" />`;
      } catch (e) { /* il codice fiscale in chiaro e sufficiente */ }
      body += `</div>`;
    }

    // Documenti di identita in coda
    if (documenti.length > 0) {
      body += `<p class="section-title">Documenti di Identita</p>`;
      documenti.forEach(d => {
        const tipoLabel = d.tipo === 'carta_identita' ? "Carta d'Identita" : d.tipo === 'codice_fiscale' ? 'Tessera Codice Fiscale' : d.tipo === 'tessera_sanitaria' ? 'Tessera Sanitaria' : d.tipo === 'patente' ? 'Patente' : d.tipo;
        body += `<div style="margin-bottom:8px"><p style="font-size:10px;font-weight:bold;margin-bottom:3px">${tipoLabel}${d.nome_file ? ' - ' + d.nome_file : ''}</p>`;
        if (d.contenuto_base64) {
          const b64 = d.contenuto_base64;
          if (b64.startsWith('data:image')) {
            body += `<img src="${b64}" style="max-width:70%;max-height:220px;border:1px solid #e2e8f0;border-radius:6px" />`;
          } else if (b64.startsWith('data:application/pdf')) {
            body += `<p style="font-size:9px;color:#6366f1;font-style:italic">Documento PDF allegato (non stampabile in anteprima)</p>`;
          } else {
            body += `<img src="data:image/jpeg;base64,${b64}" style="max-width:70%;max-height:220px;border:1px solid #e2e8f0;border-radius:6px" onerror="this.style.display='none'" />`;
          }
        }
        body += `</div>`;
      });
    }

    body += `<p style="font-size:9px;color:#64748b;margin-top:10px;border-top:1px solid #e2e8f0;padding-top:4px">
      Stampata il ${stampa.toLocaleDateString('it-IT')} alle ${stampa.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })} da ${data.generato_da || '-'} — dati aggiornati al momento della stampa.
    </p>`;

    openPrintWindow('Scheda Emergenza', body, { subtitle: `${o.cognome} ${o.nome} - Camera ${o.stanza}/${o.letto}` });
  };

  return (
    <>
      <Button onClick={handleOpen} size="sm" className="bg-red-600 hover:bg-red-700 text-white no-print" data-testid="emergency-btn">
        <AlertTriangle className="w-4 h-4 mr-1" />Emergenza
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />Scheda Emergenza
            </DialogTitle>
          </DialogHeader>

          {loading ? <p className="text-center py-8 text-slate-400">Caricamento...</p> : !o ? <p className="text-center py-8 text-red-500">Errore caricamento dati</p> : (
            <>
              <div className="flex justify-end gap-2 mb-2">
                <Button onClick={handlePrint} className="bg-red-600 hover:bg-red-700 text-white" data-testid="print-emergency-btn">
                  <Printer className="w-4 h-4 mr-1" />Stampa Immediata
                </Button>
              </div>

              <div className="space-y-4 text-sm">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-3">
                  <div className="flex-1">
                    <p className="font-bold text-red-700">SCHEDA EMERGENZA - {o.cognome?.toUpperCase()} {o.nome?.toUpperCase()}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Nata/o il {dataIt(o.data_nascita)} · Camera {o.stanza} letto {o.letto}{areaLabel ? ` · ${areaLabel}` : ''}
                    </p>
                  </div>
                  {o.foto_base64 && <img src={o.foto_base64} alt="foto ospite" className="w-14 h-[70px] object-cover rounded border" data-testid="emergency-foto" />}
                </div>

                {/* ALLERGIE - sempre visibile */}
                <div className="bg-red-50 border-2 border-red-400 rounded-lg p-3" data-testid="emergency-allergie">
                  <div className="flex items-center gap-2 mb-1.5">
                    <ShieldAlert className="w-4 h-4 text-red-600" />
                    <span className="text-xs font-bold text-red-700 uppercase tracking-wider">Allergie / Intolleranze</span>
                  </div>
                  {allergie.length === 0 && allergieNonFarma.length === 0 ? (
                    <p className="text-xs font-bold text-red-700" data-testid="emergency-nessuna-allergia">
                      NESSUNA ALLERGIA DICHIARATA — l'assenza di allergie non e accertata: verificare prima di somministrare
                    </p>
                  ) : (
                    <div className="space-y-1.5">
                      {[...allergie, ...allergieNonFarma].map((a, i) => (
                        typeof a === 'string' ? (
                          <span key={i} className="block px-2 py-0.5 bg-red-200 text-red-800 rounded text-xs font-bold">{a}</span>
                        ) : (
                          <div key={i} className={`flex items-start gap-2 p-2 rounded border ${a.gravita === 'grave' ? 'bg-red-100 border-red-300' : 'bg-orange-50 border-orange-200'}`}>
                            <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${a.gravita === 'grave' ? 'text-red-600' : 'text-orange-500'}`} />
                            <div>
                              <span className="text-xs font-bold text-slate-800">{a.sostanza}</span>
                              {(a.categoria_atc_nome || a.tipo) && <span className="block text-[10px] text-slate-500">{a.categoria_atc_nome || a.tipo}</span>}
                              <div className="flex gap-2 mt-0.5">
                                {a.reazione && <span className="text-[10px] text-red-600">Reazione: {a.reazione}</span>}
                                {a.gravita && <span className={`text-[10px] font-bold ${a.gravita === 'grave' ? 'text-red-700' : 'text-orange-600'}`}>({a.gravita})</span>}
                              </div>
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  )}
                </div>

                {/* Farmaci critici */}
                {critici.length > 0 && (
                  <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-3" data-testid="emergency-critici">
                    <div className="flex items-center gap-2 mb-1.5">
                      <HeartPulse className="w-4 h-4 text-orange-600" />
                      <span className="text-xs font-bold text-orange-700 uppercase tracking-wider">Anticoagulanti / Antiaggreganti / Insulina</span>
                    </div>
                    <div className="space-y-1">
                      {critici.map((f, i) => (
                        <div key={i} className="text-xs">
                          <span className="inline-block w-24 font-bold text-orange-700">{f.classe}</span>
                          <strong>{f.farmaco}</strong> {f.dosaggio} — {f.al_bisogno ? 'al bisogno' : (f.orari || []).join(', ')}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Allerta clinica */}
                {(allerta?.news2?.calcolabile || (allerta?.alert || []).length > 0) && (
                  <div data-testid="emergency-allerta">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" />Allerta clinica</h4>
                    {allerta.news2?.calcolabile && (
                      <p className="text-xs mb-1"><strong>NEWS2 = {allerta.news2.punteggio}</strong> ({allerta.news2.livello}) — {allerta.news2.indicazione}</p>
                    )}
                    {(allerta.alert || []).map((a, i) => (
                      <p key={i} className="text-xs text-slate-600"><strong>{a.titolo}</strong>{a.dettaglio ? ` — ${a.dettaglio}` : ''}</p>
                    ))}
                  </div>
                )}

                {/* Diagnosi */}
                {(diagnosi.principali?.length || diagnosi.secondarie?.length || diagnosi.anamnesi?.length || diagnosi.patologie_pai?.length) > 0 && (
                  <div data-testid="emergency-diagnosi">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Diagnosi e anamnesi</h4>
                    <ul className="text-xs space-y-0.5 list-disc pl-4">
                      {(diagnosi.principali || []).map((d, i) => <li key={`p${i}`}><strong>{d}</strong></li>)}
                      {(diagnosi.patologie_pai || []).map((d, i) => <li key={`pai${i}`}>{d} <span className="text-slate-400">(PAI)</span></li>)}
                      {(diagnosi.secondarie || []).map((d, i) => <li key={`s${i}`}>{d}</li>)}
                      {(diagnosi.anamnesi || []).map((d, i) => <li key={`a${i}`} className="text-slate-500">{d}</li>)}
                    </ul>
                  </div>
                )}

                {/* Presidi */}
                {(presidi.length > 0 || data.presidi_note) && (
                  <div data-testid="emergency-presidi">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1.5"><Accessibility className="w-3.5 h-3.5" />Presidi e dispositivi</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {presidi.map((p, i) => <span key={i} className="text-[11px] bg-slate-100 border rounded-full px-2 py-0.5">{p}</span>)}
                    </div>
                    {data.presidi_note && <p className="text-xs text-slate-500 mt-1">{data.presidi_note}</p>}
                  </div>
                )}

                {/* Contenzioni */}
                {contenzioni.length > 0 && (
                  <div data-testid="emergency-contenzioni">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Contenzioni in atto</h4>
                    {contenzioni.map((c, i) => (
                      <p key={i} className="text-xs"><strong>{c.tipo}</strong> ({c.fascia}) — {c.motivazione}</p>
                    ))}
                  </div>
                )}

                {/* Autonomia */}
                {(autonomia.barthel || autonomia.coscienza || autonomia.peso != null) && (
                  <div data-testid="emergency-autonomia">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Autonomia e comunicazione</h4>
                    <p className="text-xs text-slate-700">
                      {autonomia.barthel && <>Barthel <strong>{autonomia.barthel.punteggio}/100</strong> — {autonomia.barthel.classe} · </>}
                      {autonomia.coscienza && <>AVPU: <strong>{autonomia.coscienza}</strong> · </>}
                      {autonomia.peso != null && <>Peso <strong>{autonomia.peso} kg</strong></>}
                    </p>
                  </div>
                )}

                {/* Medico e numeri utili */}
                <div data-testid="emergency-medico">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1.5"><Stethoscope className="w-3.5 h-3.5" />Medico di base e numeri utili</h4>
                  <p className="text-xs mb-1">
                    <strong>{medico.nome || o.medico_base || 'Medico non indicato'}</strong>
                    {medico.telefono ? <> — <strong>{medico.telefono}</strong></> : <span className="text-red-500"> — telefono mancante</span>}
                    {medico.origine && <span className="text-slate-400"> ({medico.origine})</span>}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-1">
                    {numeriUtili.map((n, i) => (
                      <p key={i} className="text-[11px] text-slate-600 flex items-center gap-1">
                        <Phone className="w-3 h-3 text-slate-400" />{n.nome}: <strong>{n.telefono || '-'}</strong>
                      </p>
                    ))}
                  </div>
                </div>

                {/* Contatti e rappresentanza */}
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />Contatti di emergenza</h4>
                  {decisori.length > 0 && decisori.map((r, i) => (
                    <p key={i} className="text-xs text-indigo-700"><strong className="uppercase">{r.parentela}</strong>: {r.nome} — {r.telefono || '-'}</p>
                  ))}
                  {o.referenti?.length > 0 ? (
                    <div className="space-y-1">{o.referenti.map((r, i) => (
                      <div key={i} className="text-xs"><strong>{r.nome}</strong> ({r.parentela}) - Tel: <strong>{r.telefono || '-'}</strong></div>
                    ))}</div>
                  ) : <p className="text-xs text-red-600 font-bold" data-testid="emergency-no-contatti">NESSUN CONTATTO DI EMERGENZA REGISTRATO</p>}
                </div>

                {/* Dimissioni */}
                {dimissioni.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Ultimi ricoveri / dimissioni</h4>
                    {dimissioni.map((d, i) => (
                      <p key={i} className="text-xs">{d.tipo_label} del {dataIt(d.data_documento)}{d.struttura ? ` — ${d.struttura}` : ''}</p>
                    ))}
                  </div>
                )}

                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Terapie Attive ({terapie.length})</h4>
                  {terapie.length > 0 ? (
                    <div className="space-y-1.5">{terapie.map((t, i) => {
                      const allDays = ['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'];
                      const g = t.giorni_settimana || allDays;
                      const isEveryDay = !t.giorni_settimana || g.length === 7 || t.schema_giorni === 'tutti';
                      return (
                        <div key={i} className="text-xs border-l-2 border-blue-300 pl-2">
                          <strong>{t.farmaco}</strong>{' '}
                          {t.dosaggi_per_orario && t.dosaggi_per_orario.length > 1 && new Set(t.dosaggi_per_orario.map(d => d.quantita)).size > 1
                            ? t.dosaggi_per_orario.map((d, j) => (
                              <span key={j}>{j > 0 && ', '}<span className="font-mono text-brand text-[10px]">{d.orario}</span>: {d.quantita}</span>
                            ))
                            : t.dosaggio}
                          {' '}- {t.via_somministrazione || t.via || '-'}
                          {t.al_bisogno && <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-bold">AB</span>}
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            {isEveryDay ? 'Tutti i giorni' : g.join(', ')}
                            {t.durata_inizio && <> — dal {dataIt(t.durata_inizio)}</>}
                          </div>
                        </div>
                      );
                    })}</div>
                  ) : <p className="text-xs text-slate-400">Nessuna terapia attiva</p>}
                </div>

                {/* Parametri */}
                {Object.keys(lastVitals).length > 0 && (
                  <div data-testid="emergency-vitals">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1.5">
                      <Activity className="w-3.5 h-3.5" />Parametri Vitali (ultimi {giorni} giorni)
                    </h4>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {Object.entries(lastVitals).filter(([t]) => t !== 'PA_diastolica').map(([tipo, p]) => {
                        const label = tipo === 'PA_sistolica' ? 'PA' : (PARAM_LABELS[tipo] || tipo);
                        const val = tipo === 'PA_sistolica'
                          ? `${p.valore}/${lastVitals.PA_diastolica?.valore || '-'}`
                          : `${p.valore}`;
                        const unit = tipo === 'PA_sistolica' ? 'mmHg' : (PARAM_UNITS[tipo] || '');
                        return (
                          <div key={tipo} className={`p-2 rounded border text-center ${p.fuori_range ? 'bg-red-50 border-red-300' : 'bg-slate-50 border-slate-200'}`}>
                            <div className="text-[10px] text-slate-500">{label}</div>
                            <div className={`text-sm font-bold ${p.fuori_range ? 'text-red-600' : 'text-slate-800'}`}>{val}</div>
                            <div className="text-[9px] text-slate-400">{unit}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Documenti identita in coda */}
                {documenti.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Documenti di Identita</h4>
                    <div className="space-y-2">
                      {documenti.map((d, i) => (
                        <div key={i} className="text-xs">
                          <strong>{d.tipo === 'carta_identita' ? "Carta d'Identita" : d.tipo === 'codice_fiscale' ? 'Codice Fiscale' : d.tipo === 'tessera_sanitaria' ? 'Tessera Sanitaria' : d.tipo}</strong>
                          {' - '}{d.nome_file}
                          {d.contenuto_base64?.startsWith('data:image') && (
                            <img src={d.contenuto_base64} alt={d.tipo} className="mt-1 max-w-[200px] max-h-[120px] border rounded" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-[10px] text-slate-400 border-t pt-2">
                  Dati aggiornati al momento della stampa. Anteprima generata per {data.generato_da}.
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
